import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { primitiveColors, semanticColors } from '@/theme/tokens';

export type StoreKind = 'ios' | 'android';

interface StoreBadgeProps {
  store: StoreKind;
  href: string;
  onClick?: () => void;
}

// Locales each vendor ships an official localized badge for. Anything else
// falls back to the English badge — both vendors permit it globally, and the
// service marks ("App Store" / "Google Play") stay in English regardless.
// Files live in ``public/assets/store-badges/{apple,google}/<locale>.svg`` and
// are the vendors' own downloadable artwork; recreating the lockups is not
// permitted by either brand's guidelines.
const APPLE_BADGE_LOCALES = new Set([
  'en',
  'es',
  'zh-Hans',
  'zh-Hant',
  'vi',
  'ko',
  'tl',
  'bn',
  'ja',
  'ar',
  'ur',
  'ru',
  'pt',
]);
const GOOGLE_BADGE_LOCALES = new Set([
  'en',
  'es',
  'zh-Hans',
  'zh-Hant',
  'vi',
  'ko',
  'tl',
  'km',
  'bn',
  'ja',
  'ar',
  'fa',
  'ur',
  'ru',
  'pt',
]);

// Resolve the active UI language to the closest badge we ship: exact match,
// then base language, with bare "zh" mapping to Simplified; English otherwise.
function resolveBadgeLocale(language: string, available: Set<string>): string {
  if (available.has(language)) return language;
  const base = language.split('-')[0];
  if (base === 'zh') return 'zh-Hans';
  if (available.has(base)) return base;
  return 'en';
}

/**
 * Official app-store badge rendered from Apple's and Google's downloadable
 * artwork — one localized SVG per supported locale, English fallback otherwise.
 * Both brands require using their supplied badge rather than a recreated
 * lockup, so this renders the vendor SVG as a single focusable link whose
 * accessible name is the localized "Download on the App Store" / "Get it on
 * Google Play" phrase.
 */
export default function StoreBadge({ store, href, onClick }: StoreBadgeProps) {
  const { t, i18n } = useTranslation('landing');
  const isIos = store === 'ios';
  const vendor = isIos ? 'apple' : 'google';
  const locale = resolveBadgeLocale(
    i18n.language,
    isIos ? APPLE_BADGE_LOCALES : GOOGLE_BADGE_LOCALES
  );
  // If the localized SVG 404s (missing asset, stale CDN cache), fall back to
  // the English badge rather than leaving the link with no visible image —
  // an unloaded <img> collapses to zero size on iOS Safari, taking the
  // button's tappable area with it.
  const [badgeFailed, setBadgeFailed] = useState(false);
  useEffect(() => {
    setBadgeFailed(false);
  }, [vendor, locale]);
  const effectiveLocale = badgeFailed ? 'en' : locale;
  const src = `${import.meta.env.BASE_URL}assets/store-badges/${vendor}/${effectiveLocale}.svg`;
  const label = isIos
    ? `${t('badgeAppStoreEyebrow')} ${t('badgeAppStoreName')}`
    : `${t('badgePlayEyebrow')} ${t('badgePlayName')}`;

  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      // Explicit name = localized badge phrase + new-tab hint. With this set,
      // the img is decorative (alt="") so no AT concatenates the two.
      aria-label={`${label}, ${t('opensInNewTab')}`}
      sx={{
        display: 'inline-flex',
        // Keeps the tap target present at its usual footprint even if the
        // image is still loading or has failed — a zero-size <a> around a
        // broken image is otherwise invisible to touch on iOS Safari.
        minHeight: 48,
        minWidth: 120,
        borderRadius: '10px',
        transition: 'transform 0.12s ease, opacity 0.12s ease',
        '&:hover': { transform: 'translateY(-1px)', opacity: 0.92 },
        '&:focus-visible': {
          outline: `2px solid ${primitiveColors.white}`,
          outlineOffset: '3px',
          // Purple outer ring keeps the indicator visible on any background,
          // not only the dark hero the badge sits on today.
          boxShadow: `0 0 0 5px ${semanticColors.brandAccent}`,
        },
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        onError={() => setBadgeFailed(true)}
        // Apple's min is 40px and Google's is 28px; 48px clears both and matches
        // the web CTA beside it. Width tracks each badge's own aspect ratio.
        sx={{ height: 48, width: 'auto', display: 'block' }}
      />
    </Box>
  );
}
